import produce from 'immer';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { IndexRange } from '../../../../common-types';
import { appCache } from '../../cache/cache';
import { getServiceCacheKey, Service } from '../../services/utils';
import { yieldToMain } from '../../utilFunctions/utils';
import {
  ChartDataFetchError,
  ChartDataFetchErrorStyles,
} from '../../v2/common/components/errors/components/ChartDataFetchError';
import { removeDuplicates } from '../../v2/common/utils';
import {
  useLatestVersionsContext,
  useLegacyDomainScopedStoresContext,
  useLegacyStoresContext,
} from '../../v2/context/contexts';
import { ApiMasterDataQueryFilterItem } from '../../v2/types';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from './constants';

interface RenderCallback<Response> {
  data: Response | null;
  refetch: (offsetParams?: IndexRange) => Promise<void>;
}

interface ServiceExecutorProps<Inputs, Response> {
  children: (state: RenderCallback<Response>) => React.ReactNode;
  service: Service<Inputs, Response>;
  inputs: Inputs;
  filters: ApiMasterDataQueryFilterItem[];
  withOffset?: boolean;
  styles?: {
    errorUI?: ChartDataFetchErrorStyles;
  };
}

interface ServiceExecutorState {
  data: any;
  status: Readiness;
  error: Error | null;
}

enum Readiness {
  NOT_READY = 'NOT_READY',
  ERROR = 'ERROR',
  READY = 'READY',
}

const ErrorContainer = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100px; /* Constrain height to fit in factbox space */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  overflow: visible; /* Allow slight overflow to prevent clipping */
  padding: 2px; /* Minimal padding */
  background: rgba(
    0,
    165,
    153,
    0.95
  ); /* Semi-transparent background to overlay content */
  border-radius: 4px; /* Subtle rounding */
`;

export const ServiceExecutor = observer(
  <Inputs = any, Response = any>(
    props: ServiceExecutorProps<Inputs, Response>
  ) => {
    const legacyStores = useLegacyStoresContext();
    const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
    const { loadingStateStore } = legacyDomainScopedStores;
    const latestVersions = useLatestVersionsContext();
    const { service, inputs, filters, withOffset, children } = props;

    const [dataState, setDataState] = React.useState<ServiceExecutorState>({
      data: null,
      status: Readiness.NOT_READY,
      error: null,
    });

    const requestCounterRef = useRef(0);
    const incRequestCounter = () => {
      requestCounterRef.current++;
    };

    const fetch = async (offsetParams?: IndexRange) => {
      incRequestCounter();
      const currRequestNum = requestCounterRef.current;

      setDataState((prev) => ({
        data: prev.data,
        status: Readiness.NOT_READY,
        error: null,
      }));

      try {
        const freshDataFetch = !offsetParams && withOffset;
        const updatedInputs = produce(inputs, (draft) => {
          if (withOffset) {
            (draft as any).offset = offsetParams?.startIndex ?? DEFAULT_OFFSET;
            (draft as any).limit = offsetParams
              ? offsetParams?.stopIndex - offsetParams?.startIndex
              : DEFAULT_LIMIT;
          }
        });
        loadingStateStore.loadStarted();
        const cacheKey = getServiceCacheKey(service, updatedInputs, filters);

        const response = await appCache.getFromCacheOrRequest(
          cacheKey,
          async () => {
            // This allows to split different ServiceExecutor operations into separate smaller tasks
            await yieldToMain();
            return service(
              updatedInputs,
              filters,
              latestVersions,
              legacyStores,
              legacyDomainScopedStores
            );
          }
        );

        const isLatestRequest = currRequestNum === requestCounterRef.current;

        if (isLatestRequest) {
          let updatedData: any;
          if (withOffset) {
            if (Array.isArray(response)) {
              updatedData = freshDataFetch
                ? response
                : removeDuplicates([...(dataState.data ?? []), ...response]);
            } else if (typeof response === 'object' && response !== null) {
              if (freshDataFetch) {
                updatedData = response;
              } else {
                updatedData = {};
                Object.keys(response).forEach((key) => {
                  if (key === 'recordsTotal') {
                    updatedData[key] =
                      updatedData[key] || (response as any)[key];
                  } else if (key === 'recordsLoaded') {
                    updatedData[key] =
                      dataState.data?.[key] + (response as any)[key];
                  } else {
                    updatedData[key] = removeDuplicates([
                      ...(dataState.data?.[key] ?? []),
                      ...(response as any)[key],
                    ]);
                  }
                });
              }
            }
          } else {
            updatedData = response;
          }
          setDataState({
            data: updatedData,
            status: Readiness.READY,
            error: null,
          });
        } else {
          console.log('Ignoring response - not latest request');
        }
      } catch (error) {
        if (currRequestNum === requestCounterRef.current) {
          setDataState((prev) => ({
            ...prev,
            status: Readiness.ERROR,
            error: error as Error,
          }));
        }
      } finally {
        loadingStateStore.loadFinished();
      }
    };

    useEffect(() => {
      void fetch();
    }, [inputs, filters]);

    // If we have data, show it regardless of status
    if (dataState.data !== null && dataState.data !== undefined) {
      return (
        <>
          {children({
            data: dataState.data,
            refetch: fetch,
          })}
        </>
      );
    }

    // Show error only if we have no data and status is ERROR
    if (dataState.status === Readiness.ERROR && dataState.error) {
      return (
        <ErrorContainer>
          <ChartDataFetchError
            retry={fetch}
            errorData={{ error: dataState.error }}
            styles={props.styles?.errorUI}
          />
        </ErrorContainer>
      );
    }

    return null;
  }
);

export default ServiceExecutor;
